import type { Dispatch, SetStateAction } from 'react';

import {
  Checkbox,
  type DataTableFeatures,
  DataTableViewOptionsPanel,
  type DataTableViewOptionsPanelGroup,
  Label,
  ToggleGroup,
  ToggleGroupItem
} from 'design-system';

import type { CatalogItem } from '@/entities/catalog-item';
import {
  ADDITIONAL_ITEM,
  additionalColumnsList,
  defaultColumnsList,
  moreColumnsList
} from '../../../constants';

import type { ReactTable } from '@tanstack/react-table';

const DisplaySettingsPanel = ({
  expandedData,
  setExpandedData,
  table,
  isForwardCurves,
  showDiscontinued,
  setShowDiscontinued,
  forwardPeriodsInline,
  setForwardPeriodsInline
}: {
  showDiscontinued: boolean;
  setShowDiscontinued: Dispatch<SetStateAction<boolean>>;
  expandedData: boolean;
  isForwardCurves: boolean;
  setExpandedData: (newValue: boolean) => void;
  table: ReactTable<DataTableFeatures, CatalogItem>;
  forwardPeriodsInline: boolean;
  setForwardPeriodsInline: Dispatch<SetStateAction<boolean>>;
}) => {
  const handleChangeExpandedData = (allItems: boolean) => {
    if (isForwardCurves || allItems === expandedData) return;
    setExpandedData(allItems);
    const columnIdsToChangeVisibility = new Set([
      'TimeRef',
      'PeriodType',
      'Period',
      ADDITIONAL_ITEM
    ]);
    // Flip each column (no explicit target) rather than setting it to `allItems`:
    // the period columns (TimeRef/PeriodType/Period) start hidden while
    // AdditionalItem starts visible, so they move in OPPOSITE directions on every
    // mode switch. These four columns are changed only here, and the guard above
    // skips the no-op case, so flipping keeps each one correctly in sync with the
    // view mode — setting them all to `allItems` would invert AdditionalItem.
    table
      .getAllColumns()
      .filter(column => columnIdsToChangeVisibility.has(column.id))
      .forEach(column => column.toggleVisibility());
  };

  const groups: DataTableViewOptionsPanelGroup[] = [
    {
      label: 'Default Columns',
      columnIds: defaultColumnsList.map(({ accessorKey }) => accessorKey)
    },
    ...(isForwardCurves
      ? []
      : [
          {
            label: 'Additional Columns',
            columnIds: additionalColumnsList.map(({ accessorKey }) => accessorKey)
          }
        ]),
    { label: 'More Columns', columnIds: moreColumnsList.map(({ accessorKey }) => accessorKey) }
  ];

  return (
    <DataTableViewOptionsPanel
      groups={groups}
      table={table}
    >
      <div className='flex min-w-40 flex-col gap-3'>
        {!isForwardCurves && (
          <>
            <ToggleGroup
              aria-label='Catalog row display'
              className='w-full'
              spacing={0}
              value={[expandedData ? 'all-items' : 'one-per-code']}
              variant='outline'
              onValueChange={value => handleChangeExpandedData(value.includes('all-items'))}
            >
              <ToggleGroupItem
                className='flex-1'
                value='one-per-code'
              >
                1 per code
              </ToggleGroupItem>
              <ToggleGroupItem
                className='flex-1'
                value='all-items'
              >
                All items
              </ToggleGroupItem>
            </ToggleGroup>
            <div className='flex items-start gap-2'>
              <Checkbox
                checked={forwardPeriodsInline}
                disabled={expandedData}
                id='forward-periods-inline'
                onCheckedChange={() => setForwardPeriodsInline(checked => !checked)}
              />
              <Label
                className='items-start'
                htmlFor='forward-periods-inline'
              >
                <span className='flex flex-col gap-1'>
                  <span>Forward periods inline</span>
                  <span className='text-xs font-normal text-muted-foreground'>
                    Expand rows to see periods
                  </span>
                </span>
              </Label>
            </div>
          </>
        )}
        <div className='flex items-center gap-2'>
          <Checkbox
            checked={showDiscontinued}
            id='show-discontinued-items'
            onCheckedChange={() => setShowDiscontinued(!showDiscontinued)}
          />
          <Label htmlFor='show-discontinued-items'>Show discontinued items</Label>
        </div>
      </div>
    </DataTableViewOptionsPanel>
  );
};

export default DisplaySettingsPanel;
